import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ASSETS, TECH } from "../../data/content";
import Reveal from "./Reveal";

function Counter({ value, label, testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [n, setN] = useState(0);
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (!inView) return;
    if (reduced) {
      setN(value);
      return;
    }
    let raf;
    const start = performance.now();
    const dur = 1600;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, reduced]);

  return (
    <div ref={ref} data-testid={testId} className="flex flex-col items-start gap-3">
      <span className="font-serif-display text-white text-7xl md:text-8xl leading-none font-light tabular-nums">
        {String(n).padStart(2, "0")}
      </span>
      <span className="overline">{label}</span>
    </div>
  );
}

export default function SustainableTech() {
  return (
    <section
      id="technology"
      data-testid="technology-section"
      className="relative bg-[#050B14] py-24 md:py-40 overflow-hidden"
    >
      {/* Subtle background image */}
      <div className="absolute inset-0 opacity-[0.18]">
        <img
          src={ASSETS.yacht.aerialSolar}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050B14] via-[#050B14]/70 to-[#050B14]" />
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 mb-20 md:mb-28">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-4 mb-8">
                <div className="h-px w-12 bg-[#2B5B61]" />
                <span className="overline">{TECH.overline}</span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2
                data-testid="tech-headline"
                className="font-serif-display text-white text-[clamp(2.25rem,5vw,4.5rem)] leading-[1.05] font-light tracking-[-0.02em] whitespace-pre-line"
              >
                {TECH.title}
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:col-start-9 self-end">
            <Reveal delay={0.25}>
              <p className="text-[#D4C9A8] text-base md:text-lg font-light leading-relaxed">
                {TECH.body}
              </p>
            </Reveal>
          </div>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-3 gap-6 md:gap-12 mb-20 md:mb-28 pb-16 border-b border-white/10">
          {TECH.stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1}>
              <Counter value={s.value} label={s.label} testId={`stat-${s.label.toLowerCase()}`} />
            </Reveal>
          ))}
        </div>

        {/* Features */}
        <div className="grid grid-cols-12 gap-x-6 gap-y-12">
          {TECH.features.map((f, i) => (
            <Reveal
              key={f.label}
              delay={(i % 3) * 0.1}
              className="col-span-12 md:col-span-6 lg:col-span-4 group"
            >
              <div className="flex items-baseline gap-4 mb-4">
                <span className="font-serif-display text-[#E5D3B3]/60 text-lg tabular-nums">
                  0{i + 1}
                </span>
                <div className="h-px flex-1 bg-white/10 group-hover:bg-[#E5D3B3]/50 transition-colors duration-700" />
              </div>
              <h3 className="font-serif-display text-white text-2xl md:text-3xl font-light leading-tight mb-3">
                {f.label}
              </h3>
              <p className="text-[#D4C9A8] text-sm md:text-base font-light leading-relaxed">
                {f.desc}
              </p>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p
            data-testid="tech-disclaimer"
            className="mt-20 max-w-3xl text-xs uppercase tracking-[0.18em] text-white/40 leading-loose"
          >
            * {TECH.disclaimer}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
